export type AnalysisBasis = "asFed" | "dryMatter";

export type GuaranteeQualifier = "min" | "max";

export interface PublishedNutrient {
  readonly kind: "published";
  readonly percent: number;
  readonly qualifier?: GuaranteeQualifier;
}

export interface EstimatedNutrient {
  readonly kind: "estimated";
  readonly percent: number;
}

export type UnknownNutrientReason =
  "unpublished" | "negativeRemainder" | "dryMatterBasis";

export interface UnknownNutrient {
  readonly kind: "unknown";
  readonly reason: UnknownNutrientReason;
}

export type NutrientValue =
  PublishedNutrient | EstimatedNutrient | UnknownNutrient;

export function published(
  percent: number,
  qualifier?: GuaranteeQualifier,
): PublishedNutrient {
  return qualifier === undefined
    ? { kind: "published", percent }
    : { kind: "published", percent, qualifier };
}

function unknown(reason: UnknownNutrientReason): UnknownNutrient {
  return { kind: "unknown", reason };
}

export const BALANCE_NUTRIENT_NAMES = [
  "protein",
  "fat",
  "fiber",
  "ash",
  "moisture",
  "carbohydrate",
] as const;
export type BalanceNutrientName = (typeof BALANCE_NUTRIENT_NAMES)[number];

export type NutrientName = BalanceNutrientName | "totalDietaryFiber";

interface PublishedNutrients {
  readonly protein: PublishedNutrient;
  readonly fat: PublishedNutrient;
  readonly fiber: PublishedNutrient;
  readonly ash?: PublishedNutrient;
  readonly carbohydrate?: PublishedNutrient;
  readonly totalDietaryFiber?: PublishedNutrient;
}

export type NutritionSource =
  | (PublishedNutrients & {
      readonly basis: "asFed";
      readonly moisture?: PublishedNutrient;
    })
  | (PublishedNutrients & {
      readonly basis: "dryMatter";
      readonly moisture?: undefined;
    });

const BALANCE_COMPONENTS: Record<
  AnalysisBasis,
  readonly BalanceNutrientName[]
> = {
  asFed: ["protein", "fat", "fiber", "ash", "moisture", "carbohydrate"],
  dryMatter: ["protein", "fat", "fiber", "ash", "carbohydrate"],
};

type EstimableNutrientName = "ash" | "moisture" | "carbohydrate";

function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

function estimateSingleMissingByBalance(
  source: NutritionSource,
): Partial<Record<EstimableNutrientName, EstimatedNutrient | UnknownNutrient>> {
  const components = BALANCE_COMPONENTS[source.basis];
  const missing = components.filter((name) => source[name] === undefined);
  if (missing.length !== 1) {
    return {};
  }
  const [target] = missing;
  const sumOfPublished = components
    .filter((name) => name !== target)
    .reduce((sum, name) => sum + (source[name]?.percent ?? 0), 0);
  const remainder = roundToOneDecimal(100 - sumOfPublished);
  return {
    [target]:
      remainder < 0
        ? unknown("negativeRemainder")
        : { kind: "estimated", percent: remainder },
  };
}

export class Nutrition {
  readonly basis: AnalysisBasis;
  readonly protein: PublishedNutrient;
  readonly fat: PublishedNutrient;
  readonly fiber: PublishedNutrient;
  readonly ash: NutrientValue;
  readonly moisture: NutrientValue;
  readonly carbohydrate: NutrientValue;
  readonly totalDietaryFiber?: PublishedNutrient;

  constructor(source: NutritionSource) {
    const estimated = estimateSingleMissingByBalance(source);
    this.basis = source.basis;
    this.protein = source.protein;
    this.fat = source.fat;
    this.fiber = source.fiber;
    this.ash = source.ash ?? estimated.ash ?? unknown("unpublished");
    this.carbohydrate =
      source.carbohydrate ?? estimated.carbohydrate ?? unknown("unpublished");
    this.moisture =
      source.basis === "dryMatter"
        ? unknown("dryMatterBasis")
        : (source.moisture ?? estimated.moisture ?? unknown("unpublished"));
    this.totalDietaryFiber = source.totalDietaryFiber;
  }

  percentAsFed(name: BalanceNutrientName): number | undefined {
    if (this.basis !== "asFed") {
      return undefined;
    }
    const value = this[name];
    return value.kind === "unknown" ? undefined : value.percent;
  }
}
